import type { CancelWager } from '../Cancel.js';
import type { DTOResponse } from '../executeCancelRequest.js';

export type CancelContextApi = {
  count: () => number;
  getCanceledCount: () => number;
  getCancelItems: () => WeakMap<CancelWager, { data?: DTOResponse; error?: unknown }>;
  getWagers: () => ReadonlyArray<CancelWager>;
};

/**
 * Creates a context object around the items in a cancelation process that is requested to be paid canceled.
 * The context object is used by the middlewares and can for example be used to retrieve information about total price, add thing that requires further user input etc.
 * The context object provides convenience functions around the cancelation and should not expose direct access to the items to be canceled.
 *
 * @param wagersToCancel - An array of cancelable items
 * @returns context api to be used during cancelation process
 */
export function createCancelContextApi(
  wagersToCancel: CancelWager[]
): [CancelContextApi, { setCanceledCount: (count: number) => void }] {
  const internalWagersToCancel = Object.freeze([...wagersToCancel]);
  const cancelItems = new WeakMap<CancelWager, { data?: DTOResponse; error?: unknown }>();
  for (const wager of wagersToCancel) {
    cancelItems.set(wager, {});
  }
  let canceledCount = 0;

  const count = (): number => wagersToCancel.length;

  const getCancelItems = (): WeakMap<CancelWager, { data?: DTOResponse; error?: unknown }> => cancelItems;
  const getWagers = (): ReadonlyArray<CancelWager> => internalWagersToCancel;

  const getCanceledCount = (): number => canceledCount;
  const context = Object.freeze({
    count,
    getCanceledCount,
    getCancelItems,
    getWagers,
  });

  const setCanceledCount = (cancelWagerCount: number): void => {
    canceledCount = cancelWagerCount;
  };

  const updateContext = Object.freeze({
    setCanceledCount,
  });

  return [context, updateContext];
}
